"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, Check, Globe, Puzzle, TrendingUp, UserCheck } from "lucide-react";
import { AnthropicPartnerBadge } from "@/components/ui/anthropic-partner-badge";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

// Partner logos from public directory
const lenovoLogo = "/images/partners/lenovo.png";
const adobeLogo = "/images/partners/adobe.png";
// One-ink mark from the /enterprise/ brand set — renders as a single dark
// wordmark here rather than Dell's colour lockup.
const dellLogo = "/images/brands/dell-technologies.svg";
const googleLogo = "/images/brands/google.svg";
const redHatLogo = "/images/brands/red-hat.svg";
const microsoftLogo = "/images/partners/microsoft.png";
const awsLogo = "/images/partners/aws.png";
const anthropicEditorialImage = "/images/partners/anthropic-editorial.jpg";
const openaiEditorialImage = "/images/partners/openai-editorial.jpg";

interface OurPartnersProps {
  locale?: Locale;
}

export default function OurPartners({
  locale = defaultLocale,
}: OurPartnersProps) {
  const prefersReducedMotion = useReducedMotion();
  const translate = (text: string) => t(locale, text);

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const partners: Array<{
    name: string;
    category: string;
    logo?: string;
    description: string;
    link?: string;
    // A second, unlocalised destination (e.g. the English-only Singapore page).
    // Cards with one render as a plain block holding two real links, since
    // anchors cannot nest.
    secondaryLink?: { label: string; href: string };
    featured?: boolean;
  }> = [
    {
      name: "Microsoft",
      category: "Enterprise Solutions",
      logo: microsoftLogo,
      description:
        "Authorised Microsoft reseller supplying Microsoft 365, Azure, Windows Server, Surface and GitHub Enterprise to Malaysian businesses, quoted in Ringgit.",
      link: getLocalizedPathname("/enterprise/microsoft-malaysia/", locale),
    },
    {
      name: "Amazon Web Services",
      category: "Cloud Infrastructure",
      logo: awsLogo,
      description:
        "Certified AWS partner delivering scalable cloud infrastructure, services, and solutions for enterprise digital transformation.",
      link: "https://aws.amazon.com/",
    },
    {
      name: "Lenovo",
      category: "Enterprise Hardware",
      logo: lenovoLogo,
      description:
        "Authorised Lenovo reseller supplying ThinkPad laptops, ThinkSystem servers, workstations and the ThinkStation PGX to Malaysian businesses, quoted in Ringgit.",
      link: `${getLocalizedPathname("/enterprise/enquiry/", locale)}?brand=lenovo-malaysia`,
    },
    {
      name: "Adobe Enterprise",
      category: "Creative Solutions",
      logo: adobeLogo,
      description:
        "Certified Adobe reseller providing Creative Cloud licenses, expert deployment support, and competitive enterprise pricing for Malaysian businesses.",
      link: getLocalizedPathname("/enterprise/adobe-malaysia/", locale),
      featured: true,
    },
    {
      name: "Dell Technologies",
      category: "Enterprise Hardware",
      logo: dellLogo,
      description:
        "Authorised Dell reseller supplying PowerEdge servers, GPU compute, workstations and storage — specified against the workload, quoted in Ringgit and provisioned before delivery.",
      link: getLocalizedPathname(
        "/enterprise/dell-technologies-malaysia/",
        locale,
      ),
      featured: true,
    },
    {
      name: "SonarQube",
      category: "Code Quality & Security",
      description:
        "Sonar reseller partner supplying SonarQube Cloud and Server licences, with setup, CI/CD integration and team training for development teams in Malaysia and Singapore.",
      link: getLocalizedPathname("/enterprise/sonarqube-malaysia/", locale),
      secondaryLink: { label: "SonarQube Singapore", href: "/singapore/sonarqube/" },
      featured: true,
    },
    {
      name: "Red Hat",
      category: "Enterprise Open Source",
      logo: redHatLogo,
      description:
        "Authorised Red Hat reseller supplying Red Hat Enterprise Linux, OpenShift and Ansible subscriptions to Malaysian enterprises, quoted in Ringgit.",
      link: getLocalizedPathname("/enterprise/red-hat-malaysia/", locale),
    },
    {
      name: "Alibaba Cloud",
      category: "Cloud & AI Models",
      description:
        "Authorised Alibaba Cloud reseller supplying Alibaba Cloud products and Model Studio, including Qwen models, quoted in Ringgit.",
      link: `${getLocalizedPathname("/enterprise/enquiry/", locale)}?brand=alibaba-cloud-malaysia`,
    },
    {
      name: "TrueWatch",
      category: "Observability",
      description:
        "Authorised TrueWatch reseller supplying observability for metrics, logs and traces, billed monthly in Ringgit.",
      link: `${getLocalizedPathname("/enterprise/enquiry/", locale)}?brand=truewatch-malaysia`,
    },
    {
      name: "Google Workspace",
      category: "Productivity",
      logo: googleLogo,
      description:
        "Google Workspace reseller supplying and managing Workspace licences for Malaysian businesses, with setup and migration support.",
      link: `${getLocalizedPathname("/enterprise/enquiry/", locale)}?brand=google-workspace-malaysia`,
    },
  ];

  const benefits: Array<{
    title: string;
    description: string;
    icon: LucideIcon;
  }> = [
    {
      title: "Complementary Expertise",
      description:
        "Our partnerships bring together complementary skills and knowledge, allowing us to offer more comprehensive solutions than either organization could provide independently.",
      icon: Puzzle,
    },
    {
      title: "Innovation Acceleration",
      description:
        "Collaborative partnerships accelerate innovation by combining research, development resources, and diverse perspectives to create cutting-edge solutions.",
      icon: TrendingUp,
    },
    {
      title: "Enhanced Client Value",
      description:
        "Our strategic partnerships enable us to deliver greater value to clients through best-of-breed technologies, specialized expertise, and expanded service offerings.",
      icon: UserCheck,
    },
    {
      title: "Global Reach",
      description:
        "Through our partner network, we extend our reach and capabilities globally, providing consistent service and support to clients with international operations.",
      icon: Globe,
    },
  ];

  return (
    <div className="bg-background">
      {/* Unified page header */}
      <section className="bg-background pb-16 pt-24 md:pb-20 md:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...reveal(0)}
            className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
          >
            {translate("Our Partners")}
          </motion.p>
          <motion.h1
            {...reveal(0.08)}
            className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl"
          >
            {translate("Stronger together with")}{" "}
            <span className="emphasis-underline">
              {translate("world-class partners")}
            </span>
          </motion.h1>
          <motion.p
            {...reveal(0.16)}
            className="mt-5 max-w-2xl text-lg text-neutral-muted"
          >
            {translate(
              "We believe in the power of collaboration. Our strategic partnerships with industry leaders and technology innovators enable us to deliver comprehensive, best-in-class solutions to our clients.",
            )}
          </motion.p>
        </div>
      </section>
      <div className="h-px w-full bg-border" aria-hidden />

      {/* Anthropic partnership — the page's dark proof moment */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={prefersReducedMotion ? false : { y: 12 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.21, 0.6, 0.35, 1] }}
            className="grid overflow-hidden rounded-3xl bg-hero-background lg:grid-cols-2"
          >
            <div className="flex flex-col items-start justify-center p-8 md:p-12 lg:p-14">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
                {translate("Flagship alliance")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
                {translate("Building agentic AI with Anthropic")}
              </h2>
              {/* Kept to two sentences on purpose (2026-09-30). Google had started
                  showing THIS page for "claude partner malaysia" and lifting this
                  block as our entity description, in place of the owner page.
                  So it only points there: one contextual link, exact anchor
                  "Claude partner in Malaysia", no capability roll-call and no
                  Singapore registration detail here. */}
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-primary-contrast/70">
                {translate(
                  "Anchor Sprint is Certified in the Claude Partner Network. As a ",
                )}
                <Link
                  href={getLocalizedPathname(
                    "/partners/anthropic-claude-partner/",
                    locale,
                  )}
                  className="font-semibold text-primary-contrast underline decoration-primary-contrast/40 underline-offset-4 hover:decoration-primary-contrast"
                >
                  {translate("Claude partner in Malaysia")}
                </Link>
                {translate(
                  ", we design, build and run Claude agents and train teams for official Claude certification, HRD Corp claimable.",
                )}
              </p>
              <div className="mt-7">
                <AnthropicPartnerBadge />
              </div>
            </div>
            <div className="relative min-h-[260px] lg:min-h-[360px]">
              <img
                src={anthropicEditorialImage}
                alt={translate(
                  "Abstract editorial artwork of flowing ink lines on warm paper",
                )}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* OpenAI: no badge, no logo (2026-10-08; artwork panel added 2026-10-12). This page mentions the Claude Partner
          Network, so the OpenAI badge must never render here, and no OpenAI logo
          either. Approved wording "OpenAI Select Partner"; no comparison with any
          other vendor. Points to the owner page for the OpenAI terms. */}
      <section className="bg-background pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid overflow-hidden rounded-3xl border border-border bg-card lg:grid-cols-2">
            <div className="p-8 md:p-12">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {translate("ChatGPT and the OpenAI API")}
              </p>
              <h2 className="font-display text-2xl font-bold leading-tight text-neutral-emphasis md:text-3xl">
                {translate("Part of the OpenAI Partner Network")}
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-neutral-muted">
                <span data-i18n-skip>Anchor Sprint is an OpenAI Select Partner.</span>{" "}
                {translate("As an ")}
                <Link
                  href={getLocalizedPathname("/partners/openai-partner/", locale)}
                  className="font-semibold text-primary underline underline-offset-4"
                >
                  {translate("OpenAI partner in Malaysia")}
                </Link>
                {translate(
                  ", we roll out ChatGPT Business and Enterprise, build agents on the OpenAI API, and run HRD Corp claimable ChatGPT training for teams.",
                )}
              </p>
              <p className="mb-4 mt-8 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {translate("What we do with OpenAI")}
              </p>
              <ul className="space-y-5">
                {[
                  {
                    title: "ChatGPT Business and Enterprise rollout",
                    body: "Seats, admin, SSO and data settings set up for your team.",
                    href: "/guides/chatgpt-plans-malaysia/",
                  },
                  {
                    title: "Agents on the OpenAI API",
                    body: "Custom agents connected to your own systems and data.",
                    href: "/services/ai-agents/",
                  },
                  {
                    title: "HRD Corp claimable ChatGPT training",
                    body: "Hands-on training for teams, built around real work.",
                    href: "/academy/chatgpt-training/",
                  },
                ].map((item) => (
                  <li key={item.title} className="flex gap-3">
                    <Check className="mt-1 h-5 w-5 shrink-0 text-primary" aria-hidden />
                    <div>
                      <Link
                        href={getLocalizedPathname(item.href, locale)}
                        className="font-semibold text-neutral-emphasis hover:text-primary"
                      >
                        {translate(item.title)}
                      </Link>
                      <p className="mt-1 text-sm leading-relaxed text-neutral-muted">
                        {translate(item.body)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                href={getLocalizedPathname("/partners/openai-partner/", locale)}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-neutral-emphasis transition-colors hover:border-neutral-border"
              >
                {translate("See our OpenAI partner page")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
            {/* Abstract artwork only: never an OpenAI logo or badge here,
                because this page carries the Claude Partner badge. */}
            <div className="relative min-h-[260px] lg:min-h-[360px]">
              <img
                src={openaiEditorialImage}
                alt={translate("Abstract editorial artwork of ink strokes and a teal circle on warm paper")}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Strategic partnerships — merged partner grid with real logos */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Ecosystem")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Strategic Partnerships")}
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
              {translate(
                "From cloud and hardware to AI and business software — the alliances behind every solution we deliver.",
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {partners.map((partner, index) => {
              const content = (
                <>
                  <div className="mb-5 flex h-10 items-center">
                    {partner.logo ? (
                      <img
                        src={partner.logo}
                        alt=""
                        className="h-8 max-w-[150px] object-contain object-left"
                        loading="lazy"
                      />
                    ) : (
                      // Text monogram until logo-usage rights are confirmed
                      // from the partner's portal (Sonar, Alibaba Cloud).
                      <span
                        aria-hidden
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-border font-display text-base font-semibold text-neutral-emphasis"
                      >
                        {partner.name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <h3 className="mb-1 font-display text-lg font-semibold text-neutral-emphasis">
                    {partner.name}
                  </h3>
                  <p className="mb-4 text-sm font-medium text-primary">
                    {translate(partner.category)}
                  </p>
                  <p className="text-sm leading-relaxed text-neutral-muted">
                    {translate(partner.description)}
                  </p>
                  {partner.link && partner.secondaryLink ? (
                    <span className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold text-primary">
                      <a
                        href={partner.link}
                        aria-label={translate(`Learn more about ${partner.name}`)}
                        className="inline-flex items-center gap-1.5 hover:underline"
                      >
                        {translate("Learn more")}
                        <span aria-hidden>&rarr;</span>
                      </a>
                      <a
                        href={partner.secondaryLink.href}
                        className="inline-flex items-center gap-1.5 hover:underline"
                      >
                        {translate(partner.secondaryLink.label)}
                        <span aria-hidden>&rarr;</span>
                      </a>
                    </span>
                  ) : (
                    partner.link && (
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 group-hover:gap-2.5">
                        {translate("Learn more")}
                        <span aria-hidden>&rarr;</span>
                      </span>
                    )
                  )}
                </>
              );

              const isExternal = partner.link?.startsWith("http");
              const cardClassName =
                "group flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

              return partner.link && !partner.secondaryLink ? (
                <motion.a
                  key={index}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.6,
                    delay: (index % 3) * 0.08,
                    ease: [0.21, 0.6, 0.35, 1],
                  }}
                  href={partner.link}
                  {...(isExternal && {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })}
                  className={cardClassName}
                  aria-label={translate(`Learn more about ${partner.name}`)}
                >
                  {content}
                </motion.a>
              ) : (
                <motion.div
                  key={index}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.6,
                    delay: (index % 3) * 0.08,
                    ease: [0.21, 0.6, 0.35, 1],
                  }}
                  className={cardClassName}
                >
                  {content}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership benefits */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Why it matters")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Partnership Benefits")}
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
              {translate(
                "What our partner network unlocks for the clients and projects we serve.",
              )}
            </p>
          </div>

          <div className="grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
            {benefits.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1],
                }}
                className="flex h-full items-start gap-5 rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div>
                  <h3 className="mb-2 font-display text-base font-semibold text-neutral-emphasis">
                    {translate(title)}
                  </h3>
                  <p className="text-sm leading-relaxed text-neutral-muted">
                    {translate(description)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-5xl">
            {translate("Interested in")}{" "}
            <span className="emphasis-underline">
              {translate("becoming a partner?")}
            </span>
          </h2>
          <p className="mt-5 text-lg text-neutral-muted">
            {translate(
              "We're always looking to expand our partner ecosystem with organizations that share our commitment to innovation and excellence. If you're interested in exploring partnership opportunities with Anchor Sprint, we'd love to hear from you.",
            )}
          </p>
          <div className="mt-9">
            <Link
              href={getLocalizedPathname("/contact/", locale)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {translate("Contact Us")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
